import { Link } from 'react-router';

import { toast } from '@/components/ui/use-toast';

import type {
  AgentAuthorityError,
  AgentAuthorityResponse,
} from '../heyapi/types.gen';
import { UserRoleType } from '../heyapi/types.gen';
import type { Message } from '../layout/Header/components/StandardMessage';

export const flattenAuthorityErrors = (
  data: Pick<AgentAuthorityResponse, 'authorityErrors'> | undefined | null
): AgentAuthorityError[] =>
  data?.authorityErrors ? Object.values(data.authorityErrors).flat() : [];

interface ShowAgentAuthorityNotificationsParams {
  errors: AgentAuthorityError[];
  policyId: string;
  role: UserRoleType | null;
  isPolicyReferredToMe: boolean | undefined;
  hasAgentNote: boolean;
  setHeaderMessage: (message: Message | undefined) => void;
}

function getNotificationMessage(
  errorCount: number,
  hasAgentNote: boolean
): string {
  if (errorCount > 0 && hasAgentNote) {
    return `${errorCount} agent authority alerts and agent note.`;
  }
  if (errorCount > 0) {
    return `${errorCount} Agent Authority Alerts found.`;
  }
  if (hasAgentNote) {
    return 'Agent note.';
  }
  return '';
}

export const ShowAgentAuthorityNotifications = ({
  errors,
  policyId,
  role,
  isPolicyReferredToMe,
  hasAgentNote,
  setHeaderMessage,
}: ShowAgentAuthorityNotificationsParams) => {
  if (!errors.length && !hasAgentNote) return;

  const isAgent = role === UserRoleType.AGENT;
  const message = getNotificationMessage(errors.length, hasAgentNote);

  // DC-2518: Agent Authority Error Display Logic
  // ------------------------------------------
  // 1. If user is an Agent: Show toast notification with link to review errors
  //    and clear any header message.
  // 2. If user is NOT an Agent:
  //    a. If the policy was referred to the current user (Underwriter or above):
  //       Show toast notification (same as agents) - they need to review it.
  //    b. If the policy was NOT referred to current user:
  //       Show a less intrusive header message instead of toast.

  // Agent or policy referred to current user: show toast
  if (isAgent || isPolicyReferredToMe) {
    toast({
      title: 'Agent Authority Alerts',
      description: (
        <div className="mt-2 w-[340px] rounded-md bg-red-500 p-4">
          <p className="text-white">
            {message} Please{' '}
            <Link
              to={`/policy/${policyId}/policy-decisions?tab=agentAuthorityErrors`}
              className="underline"
            >
              click here
            </Link>{' '}
            to review
          </p>
        </div>
      ),
    });
    return;
  }

  // Non-agent without referral: show header message
  if (role !== null) {
    setHeaderMessage({
      content: (
        <p className="inline-block">
          Please note, {message.toLowerCase()} Please click{' '}
          <Link
            to={`/policy/${policyId}/policy-decisions?tab=agentAuthorityErrors`}
            className="underline"
          >
            here
          </Link>{' '}
          to review
        </p>
      ),
      level: 'INFO',
    });
  }
};
